const FOCUSABLE_SELECTOR =
  'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';

/**
 * Determines whether an element is a button (by tag or ARIA role).
 *
 * @param element The DOM element to test.
 * @returns `true` for `<button>` elements or `role="button"`.
 */
function isButtonOrButtonRole(element: Element): boolean {
  return element.tagName === 'BUTTON' || element.getAttribute('role') === 'button';
}

/**
 * Determines whether an element is a checkbox that should be ignored when
 * deciding field order (its `id` contains `ignore`).
 *
 * @param element The DOM element to test.
 * @returns `true` for an ignorable checkbox input.
 */
function isIgnoredCheckbox(element: Element): boolean {
  if (element.tagName !== 'INPUT' || element.getAttribute('type') !== 'checkbox') return false;
  const id = element.getAttribute('id') || '';
  return id.includes('ignore');
}

/**
 * Determines whether an element should be skipped when locating the last
 * meaningful field in a form (buttons and ignored checkboxes).
 *
 * @param element The DOM element to test.
 * @returns `true` when the element does not count as a real field.
 */
function isSkippableElement(element: Element): boolean {
  return isButtonOrButtonRole(element) || isIgnoredCheckbox(element);
}

/**
 * Reports whether the named field is effectively the last focusable field in
 * its form, so pressing Enter should submit instead of advancing focus.
 *
 * @param fieldName The `name`/`id` of the field to check.
 * @returns `true` when only skippable elements follow the field.
 */
export function isLastFieldInForm(fieldName: string): boolean {
  const formElement = document.querySelector('form');
  if (!formElement) return false;

  const focusableElements = formElement.querySelectorAll(FOCUSABLE_SELECTOR);
  const currentFieldIndex = Array.from(focusableElements).findIndex(
    element => element.getAttribute('name') === fieldName || element.getAttribute('id') === fieldName
  );

  if (currentFieldIndex < 0) return false;
  if (currentFieldIndex === focusableElements.length - 1) return true;

  const remainingElements = Array.from(focusableElements).slice(currentFieldIndex + 1);
  return remainingElements.every(isSkippableElement);
}
